import {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { useTransition, animated } from "@react-spring/web";

import { Button, IconButton, Row, Tag } from "@narmi/design_system";
import { ContentCard, catcat as cc } from "cerulean";
import {
  onboarding,
  translatePromptGroup,
  translatePromptConfiguration,
} from "byzantine";
import type { OnboardingPrompt } from "byzantine/src/dbbl/businessLogic/modules/onboarding";
import {
  handlePromptNavigation,
  useOnboardingEventHandlers,
} from "../../../src/modules/guides/onboardingNavigation";
import style from "./OnboardingCard.module.scss";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";
import { useOnboardingGuide } from "../../../src/modules/guides";

interface ProgressBarProps {
  percentComplete: number;
  className?: string;
}

const ProgressBar = ({ percentComplete, className }: ProgressBarProps) => (
  <div className={cc(className, style.progressBar)}>
    <svg xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width={`${percentComplete}%`} height="100%" rx={2} />
    </svg>
  </div>
);

const PromptIcon = ({ iconName }: { iconName: string }) => (
  <div
    className={cc(
      "alignChild--center--center",
      style.iconWrapper,
      style.promptIcon
    )}
  >
    <span aria-label={iconName} className={`narmi-icon-${iconName}`} />
  </div>
);

const CheckIcon = () => (
  <div
    className={cc(
      "alignChild--center--center",
      style.iconWrapper,
      style.checkIcon
    )}
  >
    <span aria-label="completed" className="narmi-icon-check" />
  </div>
);

interface PromptRowProps {
  prompt: OnboardingPrompt;
  className?: string;
  onClickHandler?: () => void;
}

const { OnboardingPromptStatus, useOnboardingContainer } = onboarding;

const PromptRow = ({ prompt, className, onClickHandler }: PromptRowProps) => {
  const {
    description,
    title,
    link,
    status,
    icon_name: iconName,
    event_name: eventName,
  } = prompt;
  const isCompleted = status === OnboardingPromptStatus.completed;
  const navigate = useNavigate();

  const promptHandler = useCallback(() => {
    if (link) {
      handlePromptNavigation(prompt, { navigate });
    } else if (eventName) {
      onClickHandler?.();
    }
  }, [link, eventName, prompt, navigate, onClickHandler]);

  const Wrapper = ({
    wrapperClassName,
    children,
  }: {
    wrapperClassName: string;
    children: ReactNode;
  }) =>
    isCompleted ? (
      <div className={wrapperClassName}>{children}</div>
    ) : (
      <button
        className={cc(wrapperClassName, "button--reset")}
        onClick={promptHandler}
      >
        {children}
      </button>
    );

  return (
    <Wrapper
      wrapperClassName={cc(className, style.promptRow, {
        "not-completed": !isCompleted,
      })}
    >
      <Row alignItems="center">
        <Row.Item shrink>
          {isCompleted ? <CheckIcon /> : <PromptIcon iconName={iconName} />}
        </Row.Item>
        <Row.Item>
          <div
            className={cc("fontWeight--semibold padding--bottom--xxs", {
              "fontColor--heading": !isCompleted,
              "fontColor--secondary": isCompleted,
            })}
          >
            {title}
          </div>
          <div className="fontSize--s padding--bottom--xxs fontColor--secondary">
            {description}
          </div>
        </Row.Item>
        {!isCompleted && (
          <Row.Item shrink>
            <div className="alignt--center--center">
              <span className="narmi-icon-arrow-right fontColor--heading" />
            </div>
          </Row.Item>
        )}
      </Row>
    </Wrapper>
  );
};

const OnboardingCard = () => {
  const { l10n } = useLocalization();
  const institutionSettings = useInstitutionSettings();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { user_guide_type: userGuideType } = useUserFeatures();
  const isOnboardingUser = userGuideType === "onboarding";
  const { shouldShowInDashboard, moveToSidebar } = useOnboardingGuide();
  const {
    id: promptGroupId,
    title: rawTitle,
    description: rawDescription,
    prompts: rawPrompts,
    fetchOnboardingPrompts,
    uncompletedPromptsCount,
    percentComplete,
  } = useOnboardingContainer("web");

  const { eventHandlerMap, renderDialogs } = useOnboardingEventHandlers({
    onSuccess: fetchOnboardingPrompts,
  });

  const { title, description } = useMemo(
    () =>
      translatePromptGroup(
        { id: promptGroupId, title: rawTitle, description: rawDescription },
        l10n
      ),
    [promptGroupId, rawTitle, rawDescription, l10n]
  );

  const prompts = useMemo(
    () =>
      rawPrompts.map((prompt) => translatePromptConfiguration(prompt, l10n)),
    [rawPrompts, l10n]
  );

  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(1000);
  const [hasInitiallyRendered, setHasInitiallyRendered] = useState(false);
  const shouldShowCard =
    isOnboardingUser && uncompletedPromptsCount > 0 && shouldShowInDashboard;

  useEffect(() => {
    if (isOnboardingUser) {
      fetchOnboardingPrompts();
    }
  }, [isOnboardingUser, fetchOnboardingPrompts]);

  useLayoutEffect(() => {
    if (contentRef.current) {
      const height = contentRef.current.scrollHeight;
      setContentHeight(height);
    }
  }, [prompts, title, description, shouldShowCard]);

  const onRemindMeLater = useCallback(() => {
    const days = institutionSettings?.onboarding_remind_me_later_days || 5;
    moveToSidebar(days);
  }, [institutionSettings?.onboarding_remind_me_later_days, moveToSidebar]);

  const transitions = useTransition(shouldShowCard, {
    from: {
      opacity: 0,
      maxHeight: 0,
      marginBottom: -20,
      transform: "translateY(-20px)",
    },
    enter: {
      opacity: 1,
      maxHeight: contentHeight,
      marginBottom: 0,
      transform: "translateY(0)",
    },
    leave: {
      opacity: 0,
      maxHeight: 0,
      marginBottom: -20,
      transform: "translateY(0)",
    },
    config: { tension: 180, friction: 26, clamp: true },
    immediate: !hasInitiallyRendered,
    onRest: () => {
      if (shouldShowCard && !hasInitiallyRendered) {
        setHasInitiallyRendered(true);
      }
    },
  });

  return transitions(
    ({ marginBottom: marginButtonAnimation, ...animationStyles }, item) =>
      item && (
        <animated.div
          ref={contentRef}
          // Animating with the negative margin allows us to animate away the container's gap while animating to a `contentHeight` of 0.
          style={{ marginBottom: marginButtonAnimation }}
        >
          <animated.div style={{ ...animationStyles, overflow: "hidden" }}>
            <ContentCard kind="elevated">
              <div className="margin--left--xs">
                <Row alignItems="center" className={style.cardRowContainer}>
                  <Row.Item>
                    <h4 className={style.cardTitle}>{title}</h4>
                    <div className="margin--top--xs">{description}</div>
                  </Row.Item>
                  <Row.Item
                    shrink
                    className={cc(
                      "alignChild--left--center",
                      style.cardTagContainer
                    )}
                  >
                    <Tag
                      kind="subdued"
                      label={l10n.getString(
                        "onboarding-percent-complete",
                        { percent: percentComplete },
                        `${percentComplete}% completed`
                      )}
                    />
                  </Row.Item>
                  <Row.Item shrink className="alignChild--left--center">
                    <IconButton
                      label="Hide onboarding guide"
                      kind="action"
                      name={isCollapsed ? "chevron-up" : "chevron-down"}
                      onClick={() => setIsCollapsed((state) => !state)}
                    />
                  </Row.Item>
                </Row>
              </div>
              {!isCollapsed && (
                <>
                  <ProgressBar
                    percentComplete={percentComplete}
                    className="margin--y--l margin--x--xs"
                  />
                  {prompts.map((prompt) => (
                    <PromptRow
                      key={prompt.uuid}
                      prompt={prompt}
                      onClickHandler={eventHandlerMap[prompt.event_name]}
                      className="padding--y--xs padding--x--xs"
                    />
                  ))}
                  <div className="margin--y--l border--top" />
                  <div className="alignChild--right--center">
                    <span className="margin--right--s">
                      <Button
                        label="Dismiss"
                        testId="onboarding-dismiss"
                        kind="plain"
                        onClick={moveToSidebar}
                      />
                    </span>
                    <Button
                      label="Remind me later"
                      testId="onboarding-remind-me"
                      kind="primary"
                      onClick={onRemindMeLater}
                    />
                  </div>
                </>
              )}
            </ContentCard>
          </animated.div>
          {renderDialogs}
        </animated.div>
      )
  );
};

export default OnboardingCard;
